import { NavLink } from "react-router-dom";
import GroupList from "./feature/head_nurse_mgmt/GroupList";
import { useIsAdmin, useIsGroupNurse } from "@hooks/useAuth";
import type { UserData } from "@/types/user-type";
import { useState } from "react";
import AddGroupModal from "./feature/head_nurse_mgmt/AddGroupModal";
import { useQueryClient } from "@tanstack/react-query";
import type { GroupInfo } from "@vTypes";
import { useGroupSelection } from "@/hooks/useGroupSelection";

function Nav({ loginUser }: { loginUser: UserData | null }) {
  const isAdmin = useIsAdmin();
  const isGroupNurse = useIsGroupNurse(); // 그룹 어드민
  const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<GroupInfo | null>(null);
  const queryClient = useQueryClient();
  const groupid = useGroupSelection();

  const isMyGroupId =
    loginUser?.hn_auth === "HN" &&
    loginUser?.original_group_id === groupid.selectedGroupId;
  const canViewRoster =
    loginUser?.hn_auth !== "HN" || isMyGroupId || isGroupNurse;

  if (!loginUser) {
    return (
      <>
        <nav className="t-n">
          <ul>
            <li>
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `c_nav__menu ${isActive ? "active" : ""}`
                }
              >
                Aide 홈
              </NavLink>
            </li>
          </ul>
        </nav>
      </>
    );
  }

  return (
    <div className="c_gnb__container">
      {/* Group 설정 */}
      {(isAdmin || isGroupNurse) && (
        <div className="px-[14px] py-[14px]">
          <GroupList
            showAddButton={true}
            onClickAdd={() => setIsAddGroupModalOpen(true)}
            openAddGroup={isAddGroupModalOpen}
            onEdit={(group) => {
              setEditingGroup(group);
              setIsAddGroupModalOpen(true);
            }}
            nav
          />
        </div>
      )}

      {/* Nav */}
      <nav className="t-n">
        <ul>
          <li>
            <NavLink
              to="/"
              className={({ isActive }) =>
                `c_nav__menu ${isActive ? "active" : ""}`
              }
            >
              Aide 홈
            </NavLink>
          </li>

          {loginUser && (
            <>
              {/* 관리자 (마스터 어드민) */}
              {loginUser.is_master_admin && (
                <>
                  <li>
                    <NavLink
                      to="/roster_configure"
                      className={({ isActive }) =>
                        `c_nav__menu ${isActive ? "active" : ""}`
                      }
                    >
                      근무코드 설정
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/Head_nurse_management"
                      className={({ isActive }) =>
                        `c_nav__menu ${isActive ? "active" : ""}`
                      }
                    >
                      근무자 관리
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/Account_management_mworks"
                      className={({ isActive }) =>
                        `c_nav__menu ${isActive ? "active" : ""}`
                      }
                    >
                      엑셀 업로드
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/Roster_create"
                      className={({ isActive }) =>
                        `c_nav__menu ${isActive ? "active" : ""}`
                      }
                    >
                      {isAdmin ? "근무표 확인" : "근무표 만들기"}
                    </NavLink>
                  </li>
                </>
              )}

              {/* 수간호사 (마스터 아님 + 수간호사) */}
              {!loginUser.is_master_admin && loginUser.is_head_nurse && (
                <>
                  <li>
                    <NavLink
                      to="/roster_configure"
                      className={({ isActive }) =>
                        `c_nav__menu ${isActive ? "active" : ""}`
                      }
                    >
                      근무코드 설정
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/Head_nurse_management"
                      className={({ isActive }) =>
                        `c_nav__menu ${isActive ? "active" : ""}`
                      }
                    >
                      근무자 관리
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/Roster_create"
                      className={({ isActive }) =>
                        `c_nav__menu ${isActive ? "active" : ""}`
                      }
                    >
                      근무표 만들기
                    </NavLink>
                  </li>

                  {/* 그룹 관리자가 아닌 경우 항상 보임, 그룹 관리자인 경우 자신의 그룹일 때만 보임 */}
                  {loginUser?.hn_auth !== "HN" || isMyGroupId ? (
                    <>
                      <li>
                        <NavLink
                          to="/Roster_wanted"
                          className={({ isActive }) =>
                            `c_nav__menu ${isActive ? "active" : ""}`
                          }
                        >
                          원티드 작성
                        </NavLink>
                      </li>
                    </>
                  ) : null}
                  {canViewRoster && (
                    <li>
                      <NavLink
                        to="/roster_view"
                        className={({ isActive }) =>
                          `c_nav__menu ${isActive ? "active" : ""}`
                        }
                      >
                        근무표 보기
                      </NavLink>
                    </li>
                  )}
                </>
              )}

              {/* 일반 간호사 (마스터 아님) */}
              {!loginUser.is_master_admin && !loginUser.is_head_nurse && (
                <>
                  {isAdmin && (
                    <li>
                      <NavLink
                        to="/roster_configure"
                        className={({ isActive }) =>
                          `c_nav__menu ${isActive ? "active" : ""}`
                        }
                      >
                        근무코드 설정
                      </NavLink>
                    </li>
                  )}

                  {/* 그룹 관리자가 아닌 경우 항상 보임, 그룹 관리자인 경우 자신의 그룹일 때만 보임 */}
                  {loginUser?.hn_auth !== "HN" || isMyGroupId ? (
                    <>
                      <li>
                        <NavLink
                          to="/Roster_wanted"
                          className={({ isActive }) =>
                            `c_nav__menu ${isActive ? "active" : ""}`
                          }
                        >
                          원티드 작성
                        </NavLink>
                      </li>
                    </>
                  ) : null}
                  {canViewRoster && (
                    <li>
                      <NavLink
                        to="/roster_view"
                        className={({ isActive }) =>
                          `c_nav__menu ${isActive ? "active" : ""}`
                        }
                      >
                        근무표 보기
                      </NavLink>
                    </li>
                  )}
                </>
              )}
            </>
          )}
        </ul>
      </nav>

      {isAddGroupModalOpen && (
        <AddGroupModal
          onClose={() => {
            setIsAddGroupModalOpen(false);
            setEditingGroup(null);
          }}
          onCreated={() => {}}
          fetchGroups={() => {
            queryClient.invalidateQueries();
          }}
          initialData={editingGroup}
        />
      )}
    </div>
  );
}

export default Nav;
